import { ChevronRight } from '@bb-ui/icons/dist/small/ChevronRight';
import { createStyles, makeStyles, Theme } from '@bb-ui/react-library';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  SortableTableHeaderCell,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  PrimaryButton,
} from '@bb-ui/react-library/dist/components';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { ClientData } from 'App.types';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageTemplate } from 'components/PageTemplate';
import { PaginatedTable } from 'components/PaginatedTable';
import { Pagination } from 'components/Pagination';
import useRestApi from 'hooks/useRestApi';
import { get, orderBy } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiUrl } from 'utils/apiUrl';
import { AddLearnClientButton } from './AddLearnClientButton';
import { ClientsTableToolbar } from './ClientsTableToolbar';

const LooseIconButton = IconButton as any;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    updateHeaderCell: {
      paddingLeft: '5px',
    },
    enabledCell: {
      width: 20,
    },
    pageTitleContainer: {
      marginBottom: theme.spacing(4),
      display: 'flex',
      justifyContent: 'flex-end',
    },
    tableCell: {
      padding: '2px 2px 2px 0px',
      width: 'auto',
      height: 'auto',
      paddingLeft: '3px',
    },
    pagination: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      display: 'flex',
      justifyContent: 'center',
    },
    noDataContainer: {
      padding: theme.spacing(4),
    },
    noDataActions: {
      marginTop: theme.spacing(2),
    },
  }),
);

export const LearnClients: React.FunctionComponent = (props) => {
  const { t } = useTranslation();
  const classes = useStyles(props);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [searchValue, setSearchValue] = React.useState('');
  const [submittedSearch, setSubmittedSearch] = React.useState('');

  const baseUrl = apiUrl('tenancy', 'clients');

  const {
    data: clientData,
    loading: loadClientData,
    error,
    fetch,
  } = useRestApi(baseUrl, { manual: true });

  React.useEffect(() => {
    const params = {
      page: page + 1,
      pageSize,
    };

    if (submittedSearch.trim()) {
      fetch({
        url: apiUrl('tenancy', 'clients/name'),
        method: 'POST',
        data: { name: submittedSearch },
        params,
      });
    } else {
      fetch({
        url: apiUrl('tenancy', 'clients'),
        method: 'GET',
        params,
      });
    }
  }, [submittedSearch, page, pageSize, fetch]);

  const clients = React.useMemo<ClientData[]>(
    () =>
      (clientData?.items ?? []).filter(
        (client: ClientData) => !client.name.startsWith('Integration Test Client:'),
      ),
    [clientData],
  );

  const [sortParams, setSortParams] = React.useState<Partial<OnSortChangedParams>>({});

  const sortedClients = React.useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;
    if (sortColumnId && sortDirection && ['name'].includes(sortColumnId)) {
      return orderBy(
        clients,
        [
          (client) => {
            const value = get(client, sortColumnId);
            return value ? value.toLowerCase() : '';
          },
        ],
        sortDirection,
      );
    }
    return clients;
  }, [clients, sortParams]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(`clientsList.${columnId}`);
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', {
      columnLabel,
      orderLabel,
    });
  };

  React.useEffect(() => {
    if (!searchValue.trim() && submittedSearch) {
      setPage(0);
      setSubmittedSearch('');
    }
  }, [searchValue, submittedSearch]);

  const resetToDefaultList = () => {
    setSearchValue('');
    setSubmittedSearch('');
    setPage(0);
  };

  let content: React.ReactElement;

  if (error) {
    content = (
      <ErrorMessage
        data-testid="learn-clients-list-error"
        title={t('learnClients.loadError')}
        message={error.message}
        variant="block"
      />
    );
  } else if (loadClientData) {
    content = <LoadingIndicator data-testid="clients-list-init" />;
  } else if (clients.length === 0) {
    content = (
      <>
        <ClientsTableToolbar
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
          }}
          onSearchSubmit={(value) => {
            setPage(0);
            setSubmittedSearch(value);
          }}
          pageSize={pageSize}
          onPageSizeChange={(value) => {
            setPage(0);
            setPageSize(value);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          searchLabel={t('learnClients.searchLabel')}
        />

        <div className={classes.noDataContainer}>
          <Typography data-testid="learn-clients-no-data">{t('learnClients.noData')}</Typography>

          {submittedSearch && (
            <div className={classes.noDataActions}>
              <PrimaryButton onClick={resetToDefaultList}>
                {t('learnClients.resetSearch')}
              </PrimaryButton>
            </div>
          )}
        </div>
      </>
    );
  } else {
    content = (
      <>
        <ClientsTableToolbar
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
          }}
          onSearchSubmit={(value) => {
            setPage(0);
            setSubmittedSearch(value);
          }}
          pageSize={pageSize}
          onPageSizeChange={(value) => {
            setPage(0);
            setPageSize(value);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          searchLabel={t('learnClients.searchLabel')}
        />

        <PaginatedTable
          customPagination
          hideToolbar
          perPageProps={{
            value: pageSize,
            options: PAGE_SIZE_OPTIONS,
          }}
          onSortChanged={(sortParams) => {
            setSortParams(sortParams);
            return true;
          }}
          getSortChangedAriaMessage={getAriaSortMessage}
          sortedData={sortedClients}
          noMatchesMessage={(searchExpression) => t('learnClients.noMatch', { searchExpression })}
          renderHead={() => (
            <TableHead>
              <TableRow>
                <SortableTableHeaderCell id="client-list-table-header-name" columnId="name">
                  {t('learnClients.name')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell
                  id="client-list-table-header-primary-market"
                  columnId="primary-market"
                >
                  {t('learnClients.primaryMarket')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell
                  id="client-list-table-header-secondary-market"
                  columnId="secondary-market"
                >
                  {t('learnClients.secondaryMarket')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell id="client-list-table-header-city" columnId="city">
                  {t('learnClients.city')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell id="client-list-table-header-state" columnId="state">
                  {t('learnClients.state')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell id="client-list-table-header-country" columnId="country">
                  {t('learnClients.country')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell id="client-list-table-header-team" columnId="team">
                  {t('learnClients.team')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell id="client-list-table-header-contact" columnId="contact">
                  {t('learnClients.contact')}
                </SortableTableHeaderCell>
                <SortableTableHeaderCell
                  id="client-list-table-header-external-ClientIds"
                  columnId="externalIds"
                >
                  {t('learnClients.externalIds')}
                </SortableTableHeaderCell>
                <TableCell>{t('learnClients.actions')}</TableCell>
              </TableRow>
            </TableHead>
          )}
          renderRow={(row, index) => {
            const {
              id,
              name,
              tenantId,
              primaryMarket,
              secondaryMarket,
              city,
              state,
              country,
              team,
              contact,
              externalIds,
            } = row;

            return (
              <TableRow key={id} aria-rowindex={index + 1}>
                <TableCell className={classes.tableCell}>{name}</TableCell>
                <TableCell className={classes.tableCell}>{primaryMarket}</TableCell>
                <TableCell className={classes.tableCell}>{secondaryMarket}</TableCell>
                <TableCell className={classes.tableCell}>{city}</TableCell>
                <TableCell className={classes.tableCell}>{state}</TableCell>
                <TableCell className={classes.tableCell}>{country}</TableCell>
                <TableCell className={classes.tableCell}>
                  {team.map((m, i) =>
                    Object.entries(m).map(([k, v]) => (
                      <List key={`${i}-${k}`}>
                        <ListItem>
                          <ListItemText primary={k} secondary={v || 'N/A'} />
                        </ListItem>
                      </List>
                    )),
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {contact.map((c, i) =>
                    Object.entries(c).map(([k, v]) => (
                      <List key={`${i}-${k}`}>
                        <ListItem>
                          <ListItemText primary={k} secondary={v || 'N/A'} />
                        </ListItem>
                      </List>
                    )),
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {Object.entries(externalIds).map(([k, v]) => (
                    <List key={k}>
                      <ListItem>
                        <ListItemText primary={k} secondary={v} />
                      </ListItem>
                    </List>
                  ))}
                </TableCell>
                <TableCell className={classes.enabledCell}>
                  <LooseIconButton component={Link} to={`/client/${tenantId}/assets`}>
                    <Typography component="span">{t('global.view')}</Typography>
                    <ChevronRight />
                  </LooseIconButton>
                </TableCell>
              </TableRow>
            );
          }}
        />

        {clientData && (
          <Pagination
            id="learn-clients-pagination"
            className={classes.pagination}
            page={page}
            count={clientData.totalItems}
            itemsPerPage={pageSize}
            onChangePage={setPage}
            previousButtonLabel={t('global.pagination.previousButtonAriaLabel')}
            nextButtonLabel={t('global.pagination.nextButtonAriaLabel')}
            pageSelectLabel={t('global.pagination.pageSelectLabel')}
            pageSelectAriaLabel={({ page, pages }: { page: number; pages: number }) =>
              t('global.pagination.pageSelectAriaLabel', { page, pages })
            }
            ofPages={({ pages }: { pages: number }) => t('global.pagination.ofPages', { pages })}
          />
        )}
      </>
    );
  }

  return (
    <PageTemplate
      title={t('learnClients.pageTitle')}
      data-testid="client-list-page"
      headerControl={<AddLearnClientButton />}
    >
      {content}
    </PageTemplate>
  );
};

export default LearnClients;
